import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"

import TableShimmer from "components/Shared/TableShimmer"
import formatDateTime from "components/Shared/formatDateTime"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Col, Row, Table } from "reactstrap"
import {
  deleteScheduleNotification,
  getNotificationHistory,
} from "store/actions/pushNotificationAction"
import { showAlert } from "store/reducers/alertReducer"

const ScheduleNotification = ({ activeTab, t, language }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()

  const {
    loading,
    isLoading,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    notificationHistory,
  } = useSelector(state => state.PushNotifications)

  const [searchTerm, setSearchTerm] = useState("")
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: 1,
  })

  useEffect(() => {
    if (activeTab === "2") {
      fetchNotificationHistory()
    }
  }, [activeTab])

  const fetchNotificationHistory = (page = pageNumber, overrides = {}) => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: 1, // scheduled
      orderBy: Number(overrides.sortOrder ?? selectedFilters.sortOrder) || 1,
      keyword: overrides.keyword !== undefined ? overrides.keyword : searchTerm || "",
    }
    dispatch(getNotificationHistory(payload))
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const getApiCall = (page = 1) => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: 1,
      orderBy: Number(selectedFilters.sortOrder) || 1,
      keyword: searchTerm || "",
    }

    return dispatch(getNotificationHistory(payload))
      .then(action => {
        const { statusCode, status } = action?.payload || {}
        if (statusCode === 200 && status) {
          navigate(`/push-notifications?page=${page}`)
        }
      })
      .catch(() => {})
  }

  const handleSearch = () => {
    getApiCall(1)
  }

  const handleReset = () => {
    setSearchTerm("")
    setSelectedFilters({ sortOrder: 1 })
    fetchNotificationHistory(1, { keyword: "", sortOrder: 1 })
    navigate(`/push-notifications?page=1`)
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2",
    }
    dispatch(deleteScheduleNotification(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          setShowDeleteModal(false)
          setItemIdToDelete(null)
          dispatch(showAlert({ text: msg, type: "success" }))
          fetchNotificationHistory(currentPage || 1)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(() => {})
  }

  return (
    <Row>
      <Col sm="12">
        <Filters
          filters={filters}
          searchTerm={searchTerm}
          handleSearchChange={handleSearchChange}
          handleFilterChange={handleFilterChange}
          handleSearch={handleSearch}
          onReset={handleReset}
          selectedFilters={selectedFilters}
          searchBy={t("Name")}
        />
        <div className="table-responsive">
          {loading ? (
            <TableShimmer row={7} col={7} />
          ) : notificationHistory.length === 0 ? (
            <p className="text-center my-2">
              {t("No scheduled notifications found")}
            </p>
          ) : (
            <Table className="table mb-0 capitalize_td">
              <thead className="table-light full_th">
                <tr>
                  <th>{t("S. No.")}</th>
                  <th>{t("Title")}</th>
                  <th>{t("Message")}</th>
                  <th>{t("Type")}</th>
                  <th>{t("Status")}</th>
                  <th>{t("Scheduled Time")}</th>
                  <th>{t("Action")}</th>
                </tr>
              </thead>
              <tbody>
                {notificationHistory.map(
                  (
                    {
                      _id,
                      title,
                      body,
                      type,
                      status,
                      scheduledTime,
                    },
                    index
                  ) => (
                    <tr key={_id}>
                      <td>{(currentPage - 1) * 10 + index + 1}</td>
                      <td>
                        {language === "sa" ? title?.ar : title?.en || "-"}
                      </td>
                      <td>
                        {language === "sa" ? body?.ar : body?.en || "-"}
                      </td>
                      <td>
                        {type === "all" && t("All")}
                        {type === "specific-customers" &&
                          t("Specific Customers")}
                      </td>
                      <td>{t(status)}</td>
                      <td>{formatDateTime(scheduledTime)}</td>
                      <td>
                        <i
                          onClick={e => {
                            e.stopPropagation()
                            setItemIdToDelete(_id)
                            setShowDeleteModal(true)
                          }}
                          className="mdi mdi-trash-can font-size-16 text-danger me-1 cursor-pointer"
                          title={t("Delete")}
                        ></i>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          )}
        </div>
        {!loading && (
          <div className="mt-4">
            <Paginations
              data={notificationHistory}
              itemsPerPage={itemsPerPage}
              onPageChange={onPageChange}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
            />
          </div>
        )}
      </Col>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setShowDeleteModal(false)
          setItemIdToDelete(null)
        }}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to remove this Notification?")}
      />
    </Row>
  )
}

export default ScheduleNotification
